/**
 * Settings - Templates Page
 * 
 * Manage invoice templates with visual builder
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, MoreVertical, Edit, Copy, Trash2, Star, Eye, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { EmailTemplateEditor, EMAIL_TEMPLATE_TYPE_LABELS } from '@/components/billing/EmailTemplateEditor';
import type { EmailTemplate, EmailTemplateEditorHandle } from '@/components/billing/EmailTemplateEditor';
import { appendAuditEntry } from '@/lib/audit-log-storage';
import {
  createEmailTemplate as apiCreateEmailTemplate,
  updateEmailTemplate as apiUpdateEmailTemplate,
  deleteEmailTemplate as apiDeleteEmailTemplate,
  setEmailTemplateDefault as apiSetEmailTemplateDefault,
} from '@/services/emailTemplateService';
import type { InvoiceTemplate } from '@/lib/invoice-template-types';
import { createDefaultTemplate } from '@/lib/invoice-template-types';
import {
  mockPreviewData,
  duplicateTemplate,
} from '@/lib/mock-invoice-templates';
import { useInvoiceTemplates } from '@/contexts/InvoiceTemplateContext';
import { useEmailTemplates } from '@/contexts/EmailTemplateContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { useWizardMode } from '@/contexts/WizardModeContext';
import { useAuth } from '@/providers/AuthProvider';
import { InvoiceTemplateBuilder } from '@/components/billing/InvoiceTemplateBuilder';
import type { InvoiceTemplateBuilderHandle } from '@/components/billing/InvoiceTemplateBuilder';
import { InvoicePreview } from '@/components/billing/InvoicePreview';
import { SettingsLayout } from '@/components/Pages/SettingsLayout';

interface SettingsTemplatesProps {
  defaultTab?: 'invoice-templates' | 'email-templates';
}

export default function SettingsTemplates({ defaultTab }: SettingsTemplatesProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();

  const { templates, reloadTemplates, addTemplate, updateTemplate, updateTemplateLocal, deleteTemplate, setDefault } = useInvoiceTemplates();
  // Email templates come from the shared context so Settings and the project
  // creation selectors always render the same list.
  const { emailTemplates, setEmailTemplates, reloadTemplates: reloadEmailTemplates } = useEmailTemplates();
  const { isDemoMode } = useDataMode();
  const { isWizardMode, registerSave } = useWizardMode();
  const { authenticatedUser } = useAuth();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';
  const [activeTab, setActiveTab] = useState<'invoice-templates' | 'email-templates'>(defaultTab ?? 'invoice-templates');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<InvoiceTemplate | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<InvoiceTemplate | null>(null);
  const [editedTemplateData, setEditedTemplateData] = useState<Partial<InvoiceTemplate> | null>(null);
  const [builderDirty, setBuilderDirty] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const builderRef = useRef<InvoiceTemplateBuilderHandle>(null);
  const emailEditorRef = useRef<EmailTemplateEditorHandle>(null);
  const dropdownActionRef = useRef(false);
  // Guards a second save started by a double click before the in-flight request
  // settles and React has applied the disabled state.
  const savingTemplateRef = useRef(false);
  const emailDropdownActionRef = useRef(false);

  const [emailEditorOpen, setEmailEditorOpen] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplate | null>(null);
  const [emailDeleteDialogOpen, setEmailDeleteDialogOpen] = useState(false);
  const [emailTemplateToDelete, setEmailTemplateToDelete] = useState<EmailTemplate | null>(null);

  const handleCreateEmailTemplate = () => {
    setSelectedEmailTemplate({ id: `new-${Date.now()}`, name: '', type: 'invoice', to: [], cc: [], bcc: [], subject: '', body: '', bodyHtml: '', isDefault: false, createdAt: new Date(), updatedAt: new Date() });
    setEmailEditorOpen(true);
  };

  const handleEditEmailTemplate = (tpl: EmailTemplate) => {
    setSelectedEmailTemplate(tpl);
    setEmailEditorOpen(true);
  };

  const handleSaveEmailTemplate = async (tpl: EmailTemplate) => {
    if (isDemoMode) {
      setEmailTemplates((prev) => {
        const idx = prev.findIndex((t) => t.id === tpl.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = tpl;
          return next;
        }
        return [...prev, tpl];
      });
      toast.success(`"${tpl.name}" has been saved.`);
      return;
    }
    // Live mode — persist via API
    const variables = JSON.stringify({ to: tpl.to, cc: tpl.cc, bcc: tpl.bcc });
    const isNew = !tpl.id || tpl.id.startsWith('new-') || tpl.id.startsWith('et-');
    try {
      if (isNew) {
        await apiCreateEmailTemplate({
          name: tpl.name,
          description: null,
          subject: tpl.subject,
          body: tpl.bodyHtml,
          category: tpl.type,
          variables,
          thumbnail: null,
          isDefault: tpl.isDefault,
          attachInvoiceCopies: false,
        });
      } else {
        const numericId = Number(tpl.id);
        await apiUpdateEmailTemplate(numericId, {
          id: numericId,
          name: tpl.name,
          description: null,
          subject: tpl.subject,
          body: tpl.bodyHtml,
          category: tpl.type,
          variables,
          thumbnail: null,
          isDefault: tpl.isDefault,
          attachInvoiceCopies: false,
          createdBy: null,
          updatedBy: null,
          organisationId: null,
          companyId: null,
          createdAt: tpl.createdAt.toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      toast.success(`"${tpl.name}" has been saved.`);
      await reloadEmailTemplates();
    } catch (err) {
      console.error('Failed to save email template:', err);
      toast.error('Failed to save email template.');
    }
  };

  const handleDuplicateEmailTemplate = async (tpl: EmailTemplate) => {
    if (isDemoMode) {
      const copy: EmailTemplate = { ...tpl, id: `et-${Date.now()}`, name: `${tpl.name} (Copy)`, isDefault: false, createdAt: new Date(), updatedAt: new Date() };
      setEmailTemplates((prev) => [...prev, copy]);
      toast.success(`"${tpl.name}" has been duplicated.`);
      return;
    }
    const variables = JSON.stringify({ to: tpl.to, cc: tpl.cc, bcc: tpl.bcc });
    try {
      await apiCreateEmailTemplate({
        name: `${tpl.name} (Copy)`,
        description: null,
        subject: tpl.subject,
        body: tpl.bodyHtml,
        category: tpl.type,
        variables,
        thumbnail: null,
        isDefault: false,
        attachInvoiceCopies: false,
      });
      toast.success(`"${tpl.name}" has been duplicated.`);
      await reloadEmailTemplates();
    } catch (err) {
      console.error('Failed to duplicate email template:', err);
      toast.error('Failed to duplicate email template.');
    }
  };

  const handleSetDefaultEmailTemplate = async (tpl: EmailTemplate) => {
    if (isDemoMode) {
      setEmailTemplates((prev) => prev.map((t) => ({ ...t, isDefault: t.id === tpl.id })));
      toast.success(`"${tpl.name}" is now the default email template.`);
      return;
    }
    try {
      await apiSetEmailTemplateDefault(Number(tpl.id));
      toast.success(`"${tpl.name}" is now the default email template.`);
      await reloadEmailTemplates();
    } catch (err) {
      console.error('Failed to set default email template:', err);
      toast.error('Failed to set default email template.');
    }
  };

  const handleDeleteEmailTemplate = (tpl: EmailTemplate) => {
    setEmailTemplateToDelete(tpl);
    setEmailDeleteDialogOpen(true);
  };

  const confirmDeleteEmailTemplate = async () => {
    if (!emailTemplateToDelete) return;
    if (isDemoMode) {
      setEmailTemplates((prev) => prev.filter((t) => t.id !== emailTemplateToDelete.id));
      toast.success(`"${emailTemplateToDelete.name}" has been deleted.`);
      setEmailTemplateToDelete(null);
      setEmailDeleteDialogOpen(false);
      return;
    }
    try {
      await apiDeleteEmailTemplate(Number(emailTemplateToDelete.id));
      toast.success(`"${emailTemplateToDelete.name}" has been deleted.`);
      setEmailTemplateToDelete(null);
      setEmailDeleteDialogOpen(false);
      await reloadEmailTemplates();
    } catch (err) {
      console.error('Failed to delete email template:', err);
      toast.error('Failed to delete email template.');
    }
  };

  // Create new template
  const handleCreateTemplate = () => {
    setSelectedTemplate(createDefaultTemplate('New Template'));
    setBuilderOpen(true);
  };

  // Edit template — always re-fetch the latest template data from the API
  // before opening the Edit screen so changes saved by another user are picked
  // up immediately, without relying on a manual page refresh. The builder
  // initialises its internal state from the `template` prop only on mount, so
  // the fresh copy has to be resolved before it is opened. On failure we fall
  // back to the already-known template so editing is never blocked.
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null);
  const handleEditTemplate = async (template: InvoiceTemplate) => {
    setEditLoadingId(template.id);
    let templateToEdit = template;
    try {
      const latest = await reloadTemplates();
      const fresh = latest.find((t) => t.id === template.id);
      if (fresh) {
        templateToEdit = fresh;
      }
    } catch (err) {
      console.error('Failed to reload invoice templates before editing:', err);
    } finally {
      setEditLoadingId(null);
    }
    setSelectedTemplate(templateToEdit);
    setBuilderOpen(true);
  };

  // Preview template
  const handlePreviewTemplate = (template: InvoiceTemplate) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  // Duplicate template
  const handleDuplicateTemplate = async (template: InvoiceTemplate) => {
    const newTemplate = duplicateTemplate(template, `${template.name} (Copy)`);
    try {
      await addTemplate(newTemplate);
    } catch (err) {
      console.error('Failed to duplicate invoice template:', err);
      toast.error(`Failed to duplicate "${template.name}". Please try again.`);
      return;
    }
    appendAuditEntry({
      changedBy,
      category: 'Settings',
      action: 'Created',
      entityName: 'Invoice Template',
      newValue: newTemplate.name,
      detail: `Duplicated from "${template.name}"`,
    });
    toast.success(`"${template.name}" has been duplicated.`);
  };

  // Delete template
  const handleDeleteTemplate = (template: InvoiceTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (templateToDelete) {
      try {
        await deleteTemplate(templateToDelete.id);
      } catch (err) {
        console.error('Failed to delete invoice template:', err);
        toast.error(`Failed to delete "${templateToDelete.name}". Please try again.`);
        return;
      }
      appendAuditEntry({
        changedBy,
        category: 'Settings',
        action: 'Deleted',
        entityName: 'Invoice Template',
        oldValue: templateToDelete.name,
        detail: 'Deleted via Settings > Templates',
      });
      toast.success(`"${templateToDelete.name}" has been deleted.`);
      setTemplateToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  // Set as default
  const handleSetDefault = async (template: InvoiceTemplate) => {
    try {
      await setDefault(template.id);
    } catch (err) {
      console.error('Failed to set default invoice template:', err);
      toast.error(`Failed to set "${template.name}" as default. Please try again.`);
      return;
    }
    appendAuditEntry({
      changedBy,
      category: 'Settings',
      action: 'Updated',
      entityName: 'Default Invoice Template',
      newValue: template.name,
      detail: 'Set as default via Settings > Templates',
    });
    toast.success(`"${template.name}" is now the default template.`);
  };

  // Track builder changes
  const handleBuilderChange = useCallback((data: Partial<InvoiceTemplate>, isDirty: boolean) => {
    setEditedTemplateData(data);
    setBuilderDirty(isDirty);
    // Sync live builder edits into the shared context (local only) so the
    // wizard and any other consumer always reads the latest column/layout
    // configuration for preview. The API write happens once, on explicit Save
    // — persisting on every change caused unordered, racing PUTs that clobbered
    // each other and made saves unreliable across users.
    if (data.id !== undefined) {
      updateTemplateLocal(data.id, data);
    }
  }, [updateTemplateLocal]);

  // Save template — merges builder edits with original template and persists to
  // the API. The success toast is only shown once the server confirms the write;
  // on failure we surface the error and keep the builder open so nothing is lost.
  const handleSaveTemplate = async (templateOrPartial: InvoiceTemplate | Partial<InvoiceTemplate>) => {
    if (!selectedTemplate) return;
    if (savingTemplateRef.current) return;
    savingTemplateRef.current = true;
    setSavingTemplate(true);
    // Merge the partial data with the original template
    const merged: InvoiceTemplate = {
      ...selectedTemplate,
      ...templateOrPartial,
      updatedAt: new Date(),
    };
    const existingIndex = templates.findIndex((t) => t.id === merged.id);
    try {
      if (existingIndex >= 0) {
        // Update existing — awaits the API so a failed save is not shown as success
        await updateTemplate(merged.id, merged);
        appendAuditEntry({
          changedBy,
          category: 'Settings',
          action: 'Updated',
          entityName: 'Invoice Template',
          oldValue: selectedTemplate.name,
          newValue: merged.name,
          detail: 'Updated via Settings > Templates',
        });
        toast.success(`"${merged.name}" has been updated.`);
      } else {
        // Add new — awaits the API so a failed create is not shown as success
        await addTemplate(merged);
        appendAuditEntry({
          changedBy,
          category: 'Settings',
          action: 'Created',
          entityName: 'Invoice Template',
          newValue: merged.name,
          detail: 'Created via Settings > Templates',
        });
        toast.success(`"${merged.name}" has been created.`);
      }
    } catch (err) {
      console.error('Failed to save invoice template:', err);
      toast.error(`Failed to save "${merged.name}". Your changes were not saved — please try again.`);
      return;
    } finally {
      savingTemplateRef.current = false;
      setSavingTemplate(false);
    }
    setBuilderOpen(false);
    setSelectedTemplate(null);
    setEditedTemplateData(null);
    setBuilderDirty(false);
  };

  // Cancel editing — discard unsaved builder edits. Because live edits are
  // mirrored into the shared context for preview, revert the context back to
  // the original (persisted) template so cancelled changes don't linger.
  const handleCancelBuilder = () => {
    if (selectedTemplate && templates.some((t) => t.id === selectedTemplate.id)) {
      updateTemplateLocal(selectedTemplate.id, selectedTemplate);
    }
    setBuilderOpen(false);
    setSelectedTemplate(null);
    setEditedTemplateData(null);
    setBuilderDirty(false);
  };

  // Header save button — uses tracked builder changes
  const handleHeaderSave = () => {
    if (editedTemplateData) {
      handleSaveTemplate(editedTemplateData);
    } else if (selectedTemplate) {
      handleSaveTemplate(selectedTemplate);
    }
  };

  // Onboarding wizard — the wizard hides each embedded page's own save bar and
  // saves the active step from its Next button, so persist whichever template
  // editor is currently open. Without this, templates built during onboarding
  // were discarded when the step advanced.
  const wizardSaveRef = useRef<() => Promise<void>>(async () => {});
  wizardSaveRef.current = async () => {
    if (emailEditorOpen && selectedEmailTemplate) {
      await emailEditorRef.current?.save();
      return;
    }
    if (builderOpen && selectedTemplate) {
      await handleSaveTemplate(editedTemplateData ?? selectedTemplate);
    }
  };
  useEffect(() => {
    if (!isWizardMode) return;
    return registerSave(async () => { await wizardSaveRef.current(); });
  }, [isWizardMode, registerSave]);

  // Render email template editor full-page (same pattern as InvoiceTemplateBuilder)
  if (emailEditorOpen && selectedEmailTemplate) {
    return (
      <div
        className={defaultTab ? 'flex flex-col overflow-hidden' : '-m-6 flex flex-col overflow-hidden'}
        style={{ height: defaultTab ? 'calc(100vh - 11rem)' : 'calc(100vh - 3.5rem)' }}
      >
        <EmailTemplateEditor
          ref={emailEditorRef}
          template={selectedEmailTemplate}
          onSave={async (tpl) => {
            await handleSaveEmailTemplate(tpl);
            setEmailEditorOpen(false);
            setSelectedEmailTemplate(null);
            setActiveTab('email-templates');
          }}
          onClose={() => {
            setEmailEditorOpen(false);
            setSelectedEmailTemplate(null);
            setActiveTab('email-templates');
          }}
        />
      </div>
    );
  }

  // Render builder within the page layout (not full-screen)
  // Use negative margins to break out of the app-layout p-6 wrapper and use viewport height
  if (builderOpen && selectedTemplate) {
    // A brand new template has nothing persisted yet, so Save stays available
    // even before the user touches anything; for an existing template Save is
    // only enabled once the builder reports unsaved changes.
    const isExistingTemplate = templates.some((t) => t.id === selectedTemplate.id);
    return (
      <div
        className={defaultTab ? 'flex flex-col' : '-m-6 flex flex-col'}
        style={defaultTab ? undefined : { height: 'calc(100vh - 3.5rem)' }}
      >
        {/* Header */}
        <div className="border-b bg-background px-6 py-4 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {selectedTemplate.id ? 'Edit Template' : 'Create Template'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Configure your invoice template settings and preview in real-time
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCancelBuilder}
                disabled={savingTemplate}
              >
                Cancel
              </Button>
              <Button
                onClick={handleHeaderSave}
                disabled={savingTemplate || (isExistingTemplate && !builderDirty)}
              >
                {savingTemplate && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {savingTemplate ? 'Saving…' : 'Save Template'}
              </Button>
            </div>
          </div>
        </div>

        {/* Builder Content */}
        <div className={defaultTab ? 'mt-2' : 'flex-1 min-h-0 overflow-hidden'}>
          <InvoiceTemplateBuilder
            ref={builderRef}
            template={selectedTemplate}
            onSave={handleSaveTemplate}
            onChange={handleBuilderChange}
            onCancel={handleCancelBuilder}
          />
        </div>
      </div>
    );
  }

  const content = (
    <>
      <div className="container max-w-7xl py-8 px-6">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'invoice-templates' | 'email-templates')} className="space-y-8">
            {!defaultTab && (
            <TabsList className="bg-muted/50">
              <TabsTrigger value="invoice-templates" className="data-[state=active]:bg-background">
                Invoice Templates
              </TabsTrigger>
              <TabsTrigger value="email-templates" className="data-[state=active]:bg-background">
                Email Templates
              </TabsTrigger>
            </TabsList>
            )}

            {/* Invoice Templates Tab */}
            <TabsContent value="invoice-templates" className="space-y-6 mt-0" data-tour="settings-templates-list">
              {templates.length > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {templates.length} template{templates.length !== 1 ? 's' : ''}
                  </p>
                  <Button onClick={handleCreateTemplate} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                </div>
              )}

              {templates.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <div className="rounded-full bg-muted p-4 mb-4">
                      <Plus className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No templates yet</h3>
                    <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
                      Create your first invoice template to get started. Choose from our library or build your own from scratch.
                    </p>
                    <div className="flex gap-3">
                      <Button onClick={handleCreateTemplate} size="lg">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {templates.map((template) => (
                    <Card 
                      key={template.id} 
                      className="group hover:shadow-lg transition-all duration-200 hover:border-primary/50 bg-card cursor-pointer"
                      onClick={() => {
                        // Skip if a dropdown menu action just fired (e.g. Preview)
                        if (dropdownActionRef.current) {
                          dropdownActionRef.current = false;
                          return;
                        }
                        handleEditTemplate(template);
                      }}
                    >
                      <CardHeader className="pb-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <CardTitle className="text-lg font-semibold">{template.name}</CardTitle>
                              {editLoadingId === template.id && (
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                              )}
                              {template.isDefault && (
                                <Badge variant="default" className="shrink-0">
                                  <Star className="h-3 w-3 mr-1 fill-current" />
                                  Default
                                </Badge>
                              )}
                            </div>
                            <CardDescription className="text-sm leading-relaxed">
                              {template.description || 'No description provided'}
                            </CardDescription>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => { dropdownActionRef.current = true; handleEditTemplate(template); }}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Template
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { dropdownActionRef.current = true; handlePreviewTemplate(template); }}>
                                <Eye className="h-4 w-4 mr-2" />
                                Preview
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { dropdownActionRef.current = true; handleDuplicateTemplate(template); }}>
                                <Copy className="h-4 w-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              {!template.isDefault && (
                                <DropdownMenuItem onClick={() => { dropdownActionRef.current = true; handleSetDefault(template); }}>
                                  <Star className="h-4 w-4 mr-2" />
                                  Set as Default
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => { dropdownActionRef.current = true; handleDeleteTemplate(template); }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="secondary" className="text-xs">
                            {template.sections.length} sections
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {template.settings.groupBy === 'none'
                              ? 'No grouping'
                              : `By ${template.settings.groupBy}`}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t">
                          <span className="font-medium">Last updated</span>
                          <span>{new Date(template.updatedAt).toLocaleDateString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Email Templates Tab */}
            <TabsContent value="email-templates" className="space-y-6 mt-0">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {emailTemplates.length} template{emailTemplates.length !== 1 ? 's' : ''}
                </p>
                <Button onClick={handleCreateEmailTemplate} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>

              {emailTemplates.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <div className="rounded-full bg-muted p-4 mb-4">
                      <Mail className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No email templates yet</h3>
                    <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
                      Create email templates to use when sending invoices to clients.
                    </p>
                    <Button onClick={handleCreateEmailTemplate}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Template
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {emailTemplates.map((tpl) => (
                    <Card
                      key={tpl.id}
                      className="group hover:shadow-lg transition-all duration-200 hover:border-primary/50 bg-card cursor-pointer"
                      onClick={() => {
                        if (emailDropdownActionRef.current) {
                          emailDropdownActionRef.current = false;
                          return;
                        }
                        handleEditEmailTemplate(tpl);
                      }}
                    >
                      <CardHeader className="pb-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <CardTitle className="text-lg font-semibold">{tpl.name}</CardTitle>
                              {tpl.isDefault && (
                                <Badge variant="default" className="shrink-0">
                                  <Star className="h-3 w-3 mr-1 fill-current" />
                                  Default
                                </Badge>
                              )}
                            </div>
                            <CardDescription className="text-sm leading-relaxed">
                              {EMAIL_TEMPLATE_TYPE_LABELS[tpl.type]}
                            </CardDescription>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => { emailDropdownActionRef.current = true; handleEditEmailTemplate(tpl); }}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Template
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { emailDropdownActionRef.current = true; handleDuplicateEmailTemplate(tpl); }}>
                                <Copy className="h-4 w-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              {!tpl.isDefault && (
                                <DropdownMenuItem onClick={() => { emailDropdownActionRef.current = true; handleSetDefaultEmailTemplate(tpl); }}>
                                  <Star className="h-4 w-4 mr-2" />
                                  Set as Default
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => { emailDropdownActionRef.current = true; handleDeleteEmailTemplate(tpl); }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-4">
                        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                          {tpl.subject || <span className="italic">No subject configured</span>}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t">
                          <span className="font-medium">Last updated</span>
                          <span>{new Date(tpl.updatedAt).toLocaleDateString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Template Editor — rendered full-page via early return above */}

      {/* Email Template Delete Dialog */}
      <AlertDialog open={emailDeleteDialogOpen} onOpenChange={setEmailDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Email Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{emailTemplateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteEmailTemplate} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Sheet */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent side="right" className="w-full sm:max-w-4xl p-0 overflow-y-auto">
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <SheetTitle>Invoice Template Preview</SheetTitle>
            <SheetDescription>
              Preview how this template will appear on generated invoices
            </SheetDescription>
          </SheetHeader>
          <div className="p-6">
            {previewTemplate && (
              <InvoicePreview template={previewTemplate} previewData={mockPreviewData} />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );

  if (defaultTab) return content;

  return (
    <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
      <div className="flex-1 overflow-auto">
        {content}
      </div>
    </SettingsLayout>
  );
}
